import { useState } from "react";

import { MailOutlined } from "@ant-design/icons";
import {
	Alert,
	Button,
	Card,
	Flex,
	Form,
	Image,
	Input,
	Typography,
} from "antd";

import { requestPasswordReset } from "@neu/libui";
import type { ForgotPasswordComponentProps } from "@neu/libui";

import styles from "./Login.module.scss";

export function ForgotPassword({
	onNavigateLogin,
}: ForgotPasswordComponentProps) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [sent, setSent] = useState(false);

	const handleSubmit = async (values: { email: string }) => {
		setLoading(true);
		setError(null);
		try {
			await requestPasswordReset(values.email.trim());
			setSent(true);
		} catch (err: unknown) {
			setError(
				err instanceof Error ? err.message : "Something went wrong"
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Flex justify="center" align="center" style={{ minHeight: "100vh" }}>
			<Card className={styles.loginCard}>
				<div className={styles.logoContainer}>
					<Image
						src="/logo.png"
						alt="Neu Application Logo"
						preview={false}
						className={styles.appLogo}
					/>
				</div>

				{error && (
					<Alert
						message="Request failed"
						description={error}
						type="error"
						showIcon
						closable
						onClose={() => {
							setError(null);
						}}
						style={{ marginBottom: 24, borderRadius: 8 }}
					/>
				)}

				{sent ? (
					<Flex vertical gap="middle">
						<Typography.Title level={4} style={{ marginTop: 0 }}>
							Check your email
						</Typography.Title>
						<Typography.Paragraph style={{ marginBottom: 0 }}>
							If an account exists for that address, we sent
							instructions to reset your password.
						</Typography.Paragraph>
						<Button
							type="primary"
							size="large"
							block
							onClick={onNavigateLogin}
						>
							Back to sign in
						</Button>
					</Flex>
				) : (
					<Form
						name="forgot-password"
						onFinish={(values: { email: string }) =>
							void handleSubmit(values)
						}
						layout="vertical"
						autoComplete="off"
					>
						<Typography.Title level={4} style={{ marginTop: 0 }}>
							Forgot password
						</Typography.Title>
						<Typography.Paragraph
							type="secondary"
							style={{ marginBottom: 16 }}
						>
							Enter your email address and we will send you a
							reset link.
						</Typography.Paragraph>
						<Form.Item
							name="email"
							rules={[
								{
									required: true,
									message: "Please enter your email",
								},
								{
									type: "email",
									message: "Please enter a valid email",
								},
							]}
							style={{ marginBottom: 10 }}
						>
							<Input
								prefix={
									<MailOutlined
										style={{
											color: "var(--color-primary-600)",
										}}
									/>
								}
								placeholder="Email"
								size="large"
								autoFocus
								className={styles.userNameInput}
							/>
						</Form.Item>
						<Form.Item style={{ marginTop: 8 }}>
							<Button
								type="primary"
								size="large"
								htmlType="submit"
								loading={loading}
								block
								style={{
									borderRadius: 12,
									height: 42,
								}}
							>
								Send reset link
							</Button>
						</Form.Item>
						<Flex justify="center">
							<Typography.Link onClick={onNavigateLogin}>
								Back to sign in
							</Typography.Link>
						</Flex>
					</Form>
				)}
			</Card>
		</Flex>
	);
}
